import axios from 'axios';
import axiosRetry from 'axios-retry';
import { getAuth } from 'firebase/auth';
import config from '../config/environment';

// Get API base URL from configuration
const API_BASE_URL = config.API_BASE_URL;

const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Retry transient failures (App Engine cold start, gateway errors). Network errors
// (no response — including CORS errors from 502/503 without headers) retry for all
// methods; 5xx retries only for idempotent methods. Per-request: { 'axios-retry': { retries: 0 } }.
axiosRetry(api, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
});

// Add Firebase ID token to requests
api.interceptors.request.use(
    async (config) => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const token = await user.getIdToken();
                config.headers.Authorization = `Bearer ${token}`;
            }
        } catch (error) {
            console.error('Error getting Firebase token:', error);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Utility function to parse decimal strings to numbers and dates
const parseResponseFields = (data, options = {}) => {
    const {
        decimalFields = ['amount', 'total_amount', 'materials_cost', 'labor_cost', 'total_cost', 'variance'],
        dateFields = ['date', 'transaction_date']
    } = options;

    if (Array.isArray(data)) {
        return data.map(item => parseResponseFields(item, options));
    }
    if (data && typeof data === 'object') {
        const result = { ...data };
        // Parse decimal fields
        decimalFields.forEach(field => {
            if (result[field] && typeof result[field] === 'string') {
                result[field] = parseFloat(result[field]);
            }
        });
        // Parse date fields
        dateFields.forEach(field => {
            if (result[field]) {
                result[field] = new Date(result[field]);
            }
        });
        return result;
    }
    return data;
};

// Generic read operations
export const getAll = async (endpoint) => {
    try {
        const response = await api.get(`/${endpoint}/`);
        return parseResponseFields(response.data);
    } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);
        throw error;
    }
};

export const getById = async (endpoint, id) => {
    try {
        const response = await api.get(`/${endpoint}/${id}/`);
        return parseResponseFields(response.data);
    } catch (error) {
        console.error(`Error fetching ${endpoint} with id ${id}:`, error);
        throw error;
    }
};

// Generic update operation
export const update = async (endpoint, id, data) => {
    try {
        const response = await api.patch(`/${endpoint}/${id}/`, data);
        return parseResponseFields(response.data);
    } catch (error) {
        console.error(`Error updating ${endpoint} with id ${id}:`, error);
        throw error;
    }
};

// Generic create operation
export const create = async (endpoint, data) => {
    try {
        const response = await api.post(`/${endpoint}/`, data);
        return parseResponseFields(response.data);
    } catch (error) {
        console.error(`Error creating ${endpoint}:`, error);
        throw error;
    }
};

export const deleteItem = async (endpoint, id) => {
    try {
        await api.delete(`/${endpoint}/${id}/`);
    } catch (error) {
        console.error(`Error deleting ${endpoint}:`, error);
        throw error;
    }
};

// Entity-specific API calls
export const projectsApi = {
    getAll: () => getAll('projects'),
    getById: (id) => getById('projects', id),
    create: (data) => create('projects', data),
    update: (id, data) => update('projects', id, data),
    getOfferPlan: async (id) => {
        try {
            const response = await api.get(`/projects/${id}/offer_plan/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching offer plan for project ${id}:`, error);
            throw error;
        }
    },
    submitBudgetPlan: async (id, budgetPlan) => {
        try {
            const response = await api.post(`/projects/${id}/submit_budget_plan/`, {
                budget_plan: budgetPlan
            });
            return response.data;
        } catch (error) {
            console.error(`Error submitting budget plan for project ${id}:`, error);
            throw error;
        }
    },
    getActivities: async (id) => {
        try {
            const response = await api.get(`/projects/${id}/get_activities/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching activities for project ${id}:`, error);
            throw error;
        }
    },
    customerTrackingSummary: async (id) => {
        const response = await api.get(`/projects/${id}/customer-tracking-summary/`);
        return response.data;
    },
    selectOffer: async (id, activityId, offerId) => {
        try {
            const response = await api.post(`/projects/${id}/select_offer/`, {
                activity_id: activityId,
                offer_id: offerId
            });
            return response.data;
        } catch (error) {
            console.error(`Error selecting offer for project ${id}:`, error);
            throw error;
        }
    },
    toggleActivityHidden: async (id, activityId) => {
        try {
            const response = await api.post(`/projects/${id}/toggle-activity-hidden/`, {
                activity_id: activityId,
            });
            return response.data;
        } catch (error) {
            console.error(`Error toggling hidden for project ${id} activity ${activityId}:`, error);
            throw error;
        }
    },
    updateStatus: async (id, state) => {
        try {
            const response = await api.patch(`/projects/${id}/status/`, { state });
            return response.data;
        } catch (error) {
            console.error(`Error updating project ${id} status:`, error);
            throw error;
        }
    },
    listCustomers: async (id) => {
        const response = await api.get(`/projects/${id}/customers/`);
        return response.data;
    },
    addCustomer: async (id, email) => {
        const response = await api.post(`/projects/${id}/customers/`, { email });
        return response.data;
    },
    removeCustomer: async (id, accessId) => {
        const response = await api.delete(`/projects/${id}/customers/${accessId}/`);
        return response.data;
    },
    listShared: async () => {
        const response = await api.get('/projects/shared/');
        return response.data;
    },
    getAccessPermissions: async (id) => {
        const response = await api.get(`/projects/${id}/access-permissions/`);
        return parseResponseFields(response.data);
    },
    getNotificationSettings: async (id) => {
        const response = await api.get(`/projects/${id}/settings/notifications/`);
        return response.data;
    },
    updateNotificationSettings: async (id, { setting_name, setting_key, enabled }) => {
        const response = await api.put(`/projects/${id}/settings/notifications/`, {
            setting_name,
            setting_key,
            enabled,
        });
        return response.data;
    },
};

export const projectBudgetsApi = {
    list: async (projectId) => {
        try {
            const response = await api.get('/project-budgets/', {
                params: { project_id: projectId },
            });
            return response.data;
        } catch (error) {
            console.error(`Error fetching budgets for project ${projectId}:`, error);
            throw error;
        }
    },
    get: async (budgetId) => {
        try {
            const response = await api.get(`/project-budgets/${budgetId}/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching budget ${budgetId}:`, error);
            throw error;
        }
    },
    create: async (projectId, { name, description } = {}) => {
        try {
            const response = await api.post('/project-budgets/', {
                project_id: projectId,
                name,
                ...(description ? { description } : {}),
            });
            return response.data;
        } catch (error) {
            console.error(`Error creating budget for project ${projectId}:`, error);
            throw error;
        }
    },
    selectOffer: async (budgetId, { activity_id, offer_id }) => {
        try {
            const response = await api.post(`/project-budgets/${budgetId}/select-offer/`, {
                activity_id,
                offer_id,
            });
            return response.data;
        } catch (error) {
            console.error(`Error selecting offer for budget ${budgetId}:`, error);
            throw error;
        }
    },
    removeOffer: async (budgetId, { activity_id }) => {
        try {
            const response = await api.post(`/project-budgets/${budgetId}/remove-offer/`, {
                activity_id,
            });
            return response.data;
        } catch (error) {
            console.error(`Error removing offer from budget ${budgetId}:`, error);
            throw error;
        }
    },
    setAsSelected: async (budgetId) => {
        try {
            const response = await api.post(`/project-budgets/${budgetId}/set-as-selected/`);
            return response.data;
        } catch (error) {
            console.error(`Error setting budget ${budgetId} as selected:`, error);
            throw error;
        }
    },
    updateStatus: async (budgetId, status) => {
        try {
            const response = await api.patch(`/project-budgets/${budgetId}/status/`, { status });
            return response.data;
        } catch (error) {
            console.error(`Error updating budget ${budgetId} status:`, error);
            throw error;
        }
    },
};

export const projectActivitiesApi = {
    toggleHidden: async (projectActivityId) => {
        try {
            const response = await api.post(
                `/project-activities/${projectActivityId}/toggle-hidden/`
            );
            return response.data;
        } catch (error) {
            console.error(`Error toggling hidden for project activity ${projectActivityId}:`, error);
            throw error;
        }
    },
};

export const projectDataFieldsApi = {
    getDataFields: async (id) => {
        try {
            const response = await api.get(`/projects/${id}/get_data_fields/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching data fields for project ${id}:`, error);
            throw error;
        }
    },
    saveDataFieldValue: async (id, dataFieldId, value, visibility = 'Private') => {
        try {
            const response = await api.post(`/projects/${id}/save_data_field_value/`, {
                data_field_id: dataFieldId,
                value: value,
                visibility: visibility
            });
            return response.data;
        } catch (error) {
            console.error(`Error saving data field value for project ${id}:`, error);
            throw error;
        }
    },
    deleteDataFieldValue: async (id, dataFieldId) => {
        try {
            // Use POST method since DELETE requests don't reliably support body data
            const response = await api.post(`/projects/${id}/delete_data_field_value/`, {
                data_field_id: dataFieldId
            });
            return response.data;
        } catch (error) {
            console.error(`Error deleting data field value for project ${id}:`, error);
            throw error;
        }
    },
    invalidateDocumentToken: async (id, dataFieldId, fileUrl, storagePath = null) => {
        try {
            const response = await api.post(`/projects/${id}/invalidate_document_token/`, {
                data_field_id: dataFieldId,
                file_url: fileUrl,
                storage_path: storagePath,
            });
            return response.data;
        } catch (error) {
            console.error(`Error invalidating document token for project ${id}:`, error);
            throw error;
        }
    },
    getDocumentDataFieldLinkOptions: async (id) => {
        const response = await api.get(`/projects/${id}/document_data_field_link_options/`);
        return response.data;
    },
    linkDocumentToDataField: async (id, payload) => {
        const response = await api.post(`/projects/${id}/link_document_to_data_field/`, payload);
        return response.data;
    },
    createDataField: async (id, payload) => {
        const response = await api.post(`/projects/${id}/create_data_field/`, payload);
        return response.data;
    },
};

export const accountsApi = {
    getAll: () => getAll('accounts'),
    getById: (id) => getById('accounts', id),
    create: (data) => create('accounts', data),
    update: (id, data) => update('accounts', encodeURIComponent(String(id)), data),
    delete: (id) => deleteItem('accounts', id),
};

export const projectAccountsApi = {
    getByProject: async (projectId) => {
        try {
            const response = await api.get('/project-accounts/', {
                params: { project_id: projectId },
            });
            return parseResponseFields(response.data);
        } catch (error) {
            console.error(`Error fetching project accounts for project ${projectId}:`, error);
            throw error;
        }
    },
    create: (data) => create('project-accounts', data),
    update: (id, data) => update('project-accounts', id, data),
    delete: (id) => deleteItem('project-accounts', id),
};

/**
 * Load every page of a DRF list (used where the default PAGE_SIZE would truncate totals).
 * Query params (e.g. project_id) are sent on each request.
 */
async function fetchAllPaginatedWithParams(path, baseParams = {}, pageSize = 200) {
    const results = [];
    let page = 1;
    for (;;) {
        const response = await api.get(path, {
            params: { ...baseParams, page, page_size: pageSize },
        });
        const data = response.data;
        if (data && Array.isArray(data.results)) {
            results.push(...data.results);
            if (!data.next) break;
            page += 1;
        } else {
            const rows = Array.isArray(data) ? data : [];
            return parseResponseFields(rows);
        }
    }
    return parseResponseFields(results);
}

async function fetchAllPaginated(endpoint, pageSize = 10) {
    const results = [];
    let page = 1;
    for (;;) {
        const response = await api.get(`/${endpoint}/`, {
            params: { page, page_size: pageSize },
        });
        const data = response.data;
        if (data && Array.isArray(data.results)) {
            results.push(...data.results);
            if (!data.next) break;
            page += 1;
        } else {
            const rows = Array.isArray(data) ? data : [];
            return parseResponseFields(rows);
        }
    }
    return parseResponseFields(results);
}

const normalizePaginated = (data) => {
    if (data && Array.isArray(data.results)) {
        return {
            count: data.count,
            next: data.next,
            previous: data.previous,
            results: parseResponseFields(data.results),
        };
    }
    const rows = Array.isArray(data) ? data : [];
    return {
        count: rows.length,
        next: null,
        previous: null,
        results: parseResponseFields(rows),
    };
};

export const suppliersApi = {
    /**
     * Paginated list (page, page_size, search).
     * Pass activity as numeric id[] — serialized as repeated query keys for Django getlist('activity').
     */
    list: async (params = {}) => {
        try {
            const { activity, page, page_size, search, ...rest } = params;
            const sp = new URLSearchParams();
            if (page != null) sp.set('page', String(page));
            if (page_size != null) sp.set('page_size', String(page_size));
            if (search) sp.set('search', search);
            if (Array.isArray(activity) && activity.length) {
                activity.forEach((id) => sp.append('activity', String(id)));
            }
            Object.entries(rest).forEach(([k, v]) => {
                if (v != null && v !== '') sp.set(k, String(v));
            });
            const qs = sp.toString();
            const response = await api.get(qs ? `/suppliers/?${qs}` : '/suppliers/');
            return normalizePaginated(response.data);
        } catch (error) {
            console.error('Error fetching suppliers list:', error);
            throw error;
        }
    },
    /** Loads all pages (for forms and pages that need the full list). */
    getAll: () => fetchAllPaginated('suppliers', 10),
    getById: (id) => getById('suppliers', id),
    create: (data) => create('suppliers', data),
    update: (id, data) => update('suppliers', id, data),
    delete: (id) => deleteItem('suppliers', id),
    testNotification: async (id, channel) => {
        const response = await api.post(`/suppliers/${id}/test-notification/`, { channel });
        return response.data;
    },
    linkVendor: async (id, email) => {
        const response = await api.post(`/suppliers/${id}/vendor/`, { email });
        return response.data;
    },
    unlinkVendor: async (id) => {
        await api.delete(`/suppliers/${id}/vendor/`);
    },
};

export const activitiesApi = {
    list: async (params = {}) => {
        try {
            const response = await api.get('/activities/', {
                params: { page_size: 10, ...params },
            });
            return normalizePaginated(response.data);
        } catch (error) {
            console.error('Error fetching activities list:', error);
            throw error;
        }
    },
    getAll: () => fetchAllPaginated('activities', 50),
    getById: (id) => getById('activities', id),
    create: (data) => create('activities', data),
    update: (id, data) => update('activities', id, data),
    delete: (id) => deleteItem('activities', id),
    merge: async ({ source, destination }) => {
        const response = await api.post('/activities/merge/', { source, destination });
        return response.data;
    },
};

export const servicesApi = {
    getAll: () => getAll('services'),
    getById: (id) => getById('services', id),
};

export const offersApi = {
    getAll: () => getAll('offers'),
    getById: (id) => getById('offers', id),
    create: (data) => create('offers', data),
    update: (id, data) => update('offers', id, data),
    getByProject: async (projectId) => {
        const response = await api.get(`/offers/?project_id=${projectId}`);
        return parseResponseFields(response.data);
    },
    getBySupplier: async (supplierId) => {
        const response = await api.get(`/offers/?supplier_id=${supplierId}`);
        return parseResponseFields(response.data);
    },
};

export const offerRequestsApi = {
    getAll: () => getAll('offer-requests'),
    getById: (id) => getById('offer-requests', id),
    getByProject: async (projectId) => {
        try {
            const response = await api.get(`/offer-requests/project/${projectId}/`);
            return parseResponseFields(response.data);
        } catch (error) {
            console.error(`Error fetching offer requests for project ${projectId}:`, error);
            throw error;
        }
    },
    getByHash: async (offerHash) => {
        try {
            const response = await api.get(`/offer-requests/by-hash/${offerHash}/`);
            return parseResponseFields(response.data);
        } catch (error) {
            console.error(`Error fetching offer requests by hash ${offerHash}:`, error);
            throw error;
        }
    },
    notify: async (id) => {
        const response = await api.post(`/offer-requests/${id}/notify/`);
        return response.data;
    },
};

// Public API for unauthenticated access (supplier quote submission)
export const publicOffersApi = {
    getByHash: async (offerHash) => {
        try {
            const response = await api.get(`/public-offers/by-hash/${offerHash}/`);
            return parseResponseFields(response.data);
        } catch (error) {
            console.error(`Error fetching offer requests by hash ${offerHash}:`, error);
            throw error;
        }
    },
    submitQuote: async (quoteData) => {
        try {
            const response = await api.post('/public-offers/submit-quote/', quoteData);
            return parseResponseFields(response.data);
        } catch (error) {
            console.error('Error submitting quote:', error);
            throw error;
        }
    },
};

export const vendorQuotesApi = {
    list: async () => {
        const response = await api.get('/vendor/quotes/');
        return response.data;
    },
    getByProject: async (projectId) => {
        const response = await api.get(`/vendor/quotes/${projectId}/`);
        return parseResponseFields(response.data);
    },
    getProjectInfo: async (projectId) => {
        const response = await api.get(`/vendor/quotes/${projectId}/project-info/`);
        return response.data;
    },
    submit: async (quoteData) => {
        const response = await api.post('/vendor/quotes/submit/', quoteData);
        return parseResponseFields(response.data);
    },
};

export const vendorSuppliersApi = {
    list: async () => {
        const response = await api.get('/vendor/suppliers/');
        return response.data;
    },
};

export const vendorProfileApi = {
    get: async () => {
        const response = await api.get('/vendor/profile/');
        return response.data;
    },
    update: async (data) => {
        const response = await api.patch('/vendor/profile/', data);
        return response.data;
    },
};

// Public API for unauthenticated access to project data fields
export const publicProjectDataFieldsApi = {
    getDataFields: async (projectId) => {
        try {
            const response = await api.get(`/public-project-data-fields/by-project/${projectId}/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching public data fields for project ${projectId}:`, error);
            throw error;
        }
    },
};

// QuickBooks API (Firebase auth required; QuickBooks OAuth required for data endpoints)
export const quickBooksApi = {
    getAuthStatus: async () => {
        const response = await api.get('/quickbooks/auth-status/');
        return response.data;
    },
    getAuthorizeUrl: async () => {
        const response = await api.get('/quickbooks/authorize/');
        return response.data;
    },
    getAccounts: async (params = {}) => {
        const response = await api.get('/quickbooks/accounts/', { params });
        return response.data;
    },
    getVendors: async (params = {}) => {
        const response = await api.get('/quickbooks/vendors/', { params });
        return response.data;
    },
    getCustomers: async (params = {}) => {
        const response = await api.get('/quickbooks/customers/', { params });
        return response.data;
    },
    getProjects: async (params = {}) => {
        const response = await api.get('/quickbooks/projects/', { params });
        return response.data;
    },
    getInvoices: async (params = {}) => {
        const response = await api.get('/quickbooks/invoices/', { params });
        return response.data;
    },
    getInvoiceById: async (invoiceId) => {
        const response = await api.get(`/quickbooks/invoices/${invoiceId}/`);
        return response.data;
    },
    getCustomerById: async (customerId) => {
        const response = await api.get(`/quickbooks/customers/${customerId}/`);
        return response.data;
    },
    getTransactionList: async (params = {}) => {
        const response = await api.get('/quickbooks/transaction-list/', { params });
        return response.data;
    },
    getVendorById: async (vendorId) => {
        const response = await api.get(`/quickbooks/vendors/${vendorId}/`);
        return response.data;
    },
};

export const quickBooksIntegrationApi = {
    getBulkMappings: async () => {
        const response = await api.get('/quickbooks/mappings/bulk/');
        return response.data;
    },
    getProjectMapping: async (projectId) => {
        const response = await api.get(`/quickbooks/mappings/projects/${projectId}/`);
        return response.data;
    },
    saveProjectMapping: async (projectId, payload) => {
        const response = await api.put(`/quickbooks/mappings/projects/${projectId}/`, payload);
        return response.data;
    },
    deleteProjectMapping: async (projectId) => {
        const response = await api.delete(`/quickbooks/mappings/projects/${projectId}/`);
        return response.data;
    },
    getProjectAccounts: async (projectId) => {
        const response = await api.get(`/quickbooks/mappings/projects/${projectId}/accounts/`);
        return response.data;
    },
    addProjectAccount: async (projectId, payload) => {
        const response = await api.post(`/quickbooks/mappings/projects/${projectId}/accounts/`, payload);
        return response.data;
    },
    deleteProjectAccount: async (projectId, mappingId) => {
        await api.delete(`/quickbooks/mappings/projects/${projectId}/accounts/${mappingId}/`);
    },
    getSupplierMapping: async (supplierId) => {
        const response = await api.get(`/quickbooks/mappings/suppliers/${supplierId}/`);
        return response.data;
    },
    saveSupplierMapping: async (supplierId, payload) => {
        const response = await api.put(`/quickbooks/mappings/suppliers/${supplierId}/`, payload);
        return response.data;
    },
    deleteSupplierMapping: async (supplierId) => {
        const response = await api.delete(`/quickbooks/mappings/suppliers/${supplierId}/`);
        return response.data;
    },
    suggestMappings: async ({ entityType, supplierId, projectId, locale }) => {
        const response = await api.post('/quickbooks/mappings/suggest/', {
            entity_type: entityType,
            supplier_id: supplierId,
            project_id: projectId,
            locale,
        });
        return response.data;
    },
    getReconciliationStatus: async () => {
        const response = await api.get('/quickbooks/reconciliation/status/');
        return response.data;
    },
    listReconciliationTransactions: async (params = {}) => {
        const response = await api.get('/quickbooks/reconciliation/transactions/', { params });
        return response.data;
    },
    getUsedExternalTransactionIds: async () => {
        const response = await api.get('/quickbooks/reconciliation/external-transactions/used-ids/');
        return response.data;
    },
    getDateRangeHint: async (params = {}) => {
        const response = await api.get('/quickbooks/reconciliation/date-range-hint/', { params });
        return response.data;
    },
    reconcileTransaction: async (transactionId, payload) => {
        const response = await api.post(
            `/quickbooks/reconciliation/transactions/${transactionId}/reconcile/`,
            payload,
        );
        return response.data;
    },
    unreconcileTransaction: async (transactionId) => {
        const response = await api.delete(
            `/quickbooks/reconciliation/transactions/${transactionId}/reconcile/`,
        );
        return response.data;
    },
};

export const transactionsApi = {
    /**
     * Paginated list with optional filters: account_id, supplier_id (repeat for multi),
     * activity (repeat for multi), month (1–12), year, ordering, page, page_size.
     * Array values are sent as repeated query keys (supplier_id=1&supplier_id=2).
     */
    list: async (params = {}) => {
        try {
            const flat = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (Array.isArray(value)) {
                    value.forEach((item) => flat.append(key, String(item)));
                } else {
                    flat.append(key, String(value));
                }
            });
            const response = await api.get('/transactions/', { params: flat });
            const data = response.data;
            if (data && Array.isArray(data.results)) {
                return {
                    count: data.count,
                    next: data.next,
                    previous: data.previous,
                    results: parseResponseFields(data.results),
                };
            }
            const rows = Array.isArray(data) ? data : [];
            return {
                count: rows.length,
                next: null,
                previous: null,
                results: parseResponseFields(rows),
            };
        } catch (error) {
            console.error('Error fetching transactions list:', error);
            throw error;
        }
    },
    getById: (id) => getById('transactions', id),
    getByProject: async (projectId) => {
        try {
            const response = await api.get(`/transactions/by-project/${projectId}/`);
            return parseResponseFields(response.data);
        } catch (error) {
            console.error(`Error fetching transactions for project ${projectId}:`, error);
            throw error;
        }
    },
    createAllocated: async (payload) => {
        const response = await api.post('/transactions/allocated/', payload);
        return parseResponseFields(response.data);
    },
    update: async (id, data) => {
        const response = await api.put(`/transactions/${id}/`, data);
        return parseResponseFields(response.data);
    },
    delete: (id) => deleteItem('transactions', id),
    previewGroup: async (payload) => {
        const response = await api.post('/transactions/group/preview/', payload);
        return response.data;
    },
    group: async (payload) => {
        const response = await api.post('/transactions/group/', payload);
        return parseResponseFields(response.data);
    },
    getByAccount: async (accountId) => {
        const response = await api.get('/transactions/', {
            params: { account_id: accountId, page_size: 500 },
        });
        const data = response.data;
        const rows = data.results != null ? data.results : data;
        return parseResponseFields(rows);
    },
    getBySupplier: async (supplierId) => {
        const response = await api.get('/transactions/', {
            params: { supplier_id: supplierId, page_size: 500 },
        });
        const data = response.data;
        const rows = data.results != null ? data.results : data;
        return parseResponseFields(rows);
    },
};

export const invoicesApi = {
    list: async (params = {}) => {
        const response = await api.get('/invoices/', { params });
        const data = response.data;
        if (data && Array.isArray(data.results)) {
            return {
                count: data.count,
                next: data.next,
                previous: data.previous,
                results: parseResponseFields(data.results),
            };
        }
        const rows = Array.isArray(data) ? data : [];
        return { count: rows.length, next: null, previous: null, results: parseResponseFields(rows) };
    },
    getById: (id) => getById('invoices', id),
    create: async (data) => {
        const response = await api.post('/invoices/', data);
        return parseResponseFields(response.data);
    },
    update: async (id, data) => {
        const response = await api.patch(`/invoices/${id}/`, data);
        return parseResponseFields(response.data);
    },
    remove: (id) => deleteItem('invoices', id),
    void: async (id) => {
        const response = await api.post(`/invoices/${id}/void/`);
        return parseResponseFields(response.data);
    },
    map: async (id, data) => {
        const response = await api.post(`/invoices/${id}/map/`, data);
        return parseResponseFields(response.data);
    },
    pay: async (data) => {
        const response = await api.post('/invoices/pay/', data);
        return parseResponseFields(response.data);
    },
    push: async (id) => {
        const response = await api.post(`/invoices/${id}/push/`);
        return parseResponseFields(response.data);
    },
    syncPreview: async (data = {}) => {
        const response = await api.post('/invoices/sync/preview/', data);
        return response.data;
    },
    syncApply: async (data) => {
        const response = await api.post('/invoices/sync/apply/', data);
        return response.data;
    },
    qbCustomers: async () => {
        const response = await api.get('/invoices/qb-customers/');
        return response.data;
    },
    downloadPdf: async (id, filename) => {
        const response = await api.get(`/invoices/${id}/pdf/`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `invoice-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
    customerList: async (params = {}) => {
        const response = await api.get('/invoices/customer/', { params });
        const data = response.data;
        if (data && Array.isArray(data.results)) {
            return {
                count: data.count,
                next: data.next,
                previous: data.previous,
                results: parseResponseFields(data.results),
            };
        }
        const rows = Array.isArray(data) ? data : [];
        return { count: rows.length, next: null, previous: null, results: parseResponseFields(rows) };
    },
    customerGetById: async (id) => {
        const response = await api.get(`/invoices/customer/${id}/`);
        return parseResponseFields(response.data);
    },
};

export const productsApi = {
    list: async (params = {}) => {
        const response = await api.get('/products/', { params });
        const data = response.data;
        if (data && Array.isArray(data.results)) {
            return parseResponseFields(data.results);
        }
        return parseResponseFields(Array.isArray(data) ? data : []);
    },
    getById: async (id) => {
        const response = await api.get(`/products/${id}/`);
        return parseResponseFields(response.data);
    },
    create: async (data) => {
        const response = await api.post('/products/', data);
        return parseResponseFields(response.data);
    },
    update: async (id, data) => {
        const response = await api.patch(`/products/${id}/`, data);
        return parseResponseFields(response.data);
    },
    delete: async (id) => {
        const response = await api.delete(`/products/${id}/`);
        return parseResponseFields(response.data);
    },
};

export const projectTransactionsApi = {
    getAll: () => getAll('project-transactions'),
    getById: (id) => getById('project-transactions', id),
    /**
     * Paginated list with optional filters: project_id (required), project_account_id,
     * account_id, supplier_id (repeat for multi), activity (repeat for multi),
     * month (1–12), year, ordering, page, page_size.
     */
    list: async (params = {}) => {
        try {
            const flat = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (Array.isArray(value)) {
                    value.forEach((item) => flat.append(key, String(item)));
                } else {
                    flat.append(key, String(value));
                }
            });
            const response = await api.get('/project-transactions/', { params: flat });
            const data = response.data;
            if (data && Array.isArray(data.results)) {
                return {
                    count: data.count,
                    next: data.next,
                    previous: data.previous,
                    results: parseResponseFields(data.results),
                };
            }
            const rows = Array.isArray(data) ? data : [];
            return {
                count: rows.length,
                next: null,
                previous: null,
                results: parseResponseFields(rows),
            };
        } catch (error) {
            console.error('Error fetching project transactions list:', error);
            throw error;
        }
    },
    getByProject: async (projectId) =>
        fetchAllPaginatedWithParams('/project-transactions/', { project_id: projectId }),
    getTransactionDetailForProject: async (projectId, transactionId) => {
        const response = await api.get(
            `/project-transactions/project/${encodeURIComponent(projectId)}/transactions/${transactionId}/`,
        );
        return parseResponseFields(response.data);
    },
    getByTransaction: async (transactionId) => {
        const response = await api.get(`/project-transactions/?transaction_id=${transactionId}`);
        const data = response.data;
        const rows = Array.isArray(data?.results) ? data.results : data;
        return parseResponseFields(rows);
    },
    eligibleInvoices: async (id) => {
        const response = await api.get(`/project-transactions/${id}/eligible-invoices/`);
        return parseResponseFields(Array.isArray(response.data) ? response.data : []);
    },
    applyInvoice: async (id, invoiceId, { amount, projectAccount } = {}) => {
        const payload = { invoice: invoiceId };
        if (amount != null && amount !== '') payload.amount = amount;
        if (projectAccount != null) payload.project_account = projectAccount;
        const response = await api.post(`/project-transactions/${id}/apply-invoice/`, payload);
        return parseResponseFields(response.data);
    },
    /**
     * Limited, customer-facing paginated list for a shared project.
     * Only exposes Date, project Account, gross Activity, project Amount, and
     * Direction. Supported filters: project_id (required), project_account_id,
     * activity (gross; repeat for multi), direction, project_account_type
     * (funds|revenue), month (1–12), year, ordering, page, page_size.
     */
    customerList: async (params = {}) => {
        try {
            const flat = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (Array.isArray(value)) {
                    value.forEach((item) => flat.append(key, String(item)));
                } else {
                    flat.append(key, String(value));
                }
            });
            const response = await api.get('/project-transactions/customer/', { params: flat });
            const data = response.data;
            if (data && Array.isArray(data.results)) {
                return {
                    count: data.count,
                    next: data.next,
                    previous: data.previous,
                    results: parseResponseFields(data.results),
                };
            }
            const rows = Array.isArray(data) ? data : [];
            return {
                count: rows.length,
                next: null,
                previous: null,
                results: parseResponseFields(rows),
            };
        } catch (error) {
            console.error('Error fetching customer project transactions list:', error);
            throw error;
        }
    },
    /** Distinct gross (top-level) activities available as filters for the customer view. */
    customerActivities: async (projectId) => {
        const response = await api.get('/project-transactions/customer-activities/', {
            params: { project_id: projectId },
        });
        return Array.isArray(response.data) ? response.data : [];
    },
};

export const projectDocumentFoldersApi = {
    list: async (projectId) => {
        const response = await api.get('/project-document-folders/', {
            params: { project_id: projectId },
        });
        const data = response.data;
        if (Array.isArray(data)) {
            return { folders: data, archived_count: 0 };
        }
        return {
            folders: data.folders || [],
            archived_count: data.archived_count ?? 0,
        };
    },
    create: async (data) => {
        const response = await api.post('/project-document-folders/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.patch(`/project-document-folders/${id}/`, data);
        return response.data;
    },
    delete: async (id) => {
        await api.delete(`/project-document-folders/${id}/`);
    },
};

export const projectDocumentsApi = {
    list: async (projectId, folderId = null, { archived = false } = {}) =>
        fetchAllPaginatedWithParams('/project-documents/', {
            project_id: projectId,
            ...(folderId != null ? { folder_id: folderId } : {}),
            ...(archived ? { archived: 'true' } : {}),
        }),
    get: async (id) => {
        const response = await api.get(`/project-documents/${id}/`);
        return response.data;
    },
    initiate: async (data) => {
        const response = await api.post('/project-documents/initiate/', data, {
            'axios-retry': { retries: 0 },
        });
        return response.data;
    },
    complete: async (id, params = {}) => {
        const response = await api.post(`/project-documents/${id}/complete/`, null, {
            params,
            'axios-retry': { retries: 0 },
        });
        return response.data;
    },
    update: async (id, data, params = {}) => {
        const response = await api.patch(`/project-documents/${id}/`, data, {
            params,
            'axios-retry': { retries: 0 },
        });
        return response.data;
    },
    search: async ({ query, projectId = null, pageSize = 10 } = {}) => {
        const response = await api.post(
            '/project-documents/search/',
            {
                query,
                ...(projectId != null ? { project_id: projectId } : {}),
                page_size: pageSize,
            },
            { 'axios-retry': { retries: 0 } },
        );
        return response.data;
    },
    downloadUrl: async (id) => {
        const response = await api.post(`/project-documents/${id}/download-url/`);
        return response.data;
    },
    archive: async (id) => {
        const response = await api.post(`/project-documents/${id}/archive/`);
        return response.data;
    },
    recover: async (id, { projectId, folderId = null } = {}) => {
        const response = await api.post(`/project-documents/${id}/recover/`, {
            project_id: projectId,
            ...(folderId != null ? { folder_id: folderId } : {}),
        });
        return response.data;
    },
    delete: async (id) => {
        await api.delete(`/project-documents/${id}/`);
    },
    unallocated: async () => fetchAllPaginatedWithParams('/project-documents/unallocated/', {}),
    unallocatedCount: async () => {
        const response = await api.get('/project-documents/unallocated-count/');
        return response.data?.count ?? 0;
    },
};

export const projectTemplatesApi = {
    getAll: () => getAll('project-templates'),
    getById: (id) => getById('project-templates', id),
    create: (data) => create('project-templates', data),
    update: (id, data) => update('project-templates', id, data),
    delete: (id) => deleteItem('project-templates', id),
    syncDocumentFolders: async (id) => {
        const response = await api.post(`/project-templates/${id}/sync-document-folders/`);
        return response.data;
    },
};

export const sectionsApi = {
    getAll: () => getAll('sections'),
    getById: (id) => getById('sections', id),
    create: (data) => create('sections', data),
    update: (id, data) => update('sections', id, data),
    delete: (id) => deleteItem('sections', id),
    reorder: async (sectionIds) => {
        const response = await api.post('/sections/reorder/', { section_ids: sectionIds });
        return response.data;
    },
};

export const dataFieldsApi = {
    getAll: () => getAll('data-fields'),
    getById: (id) => getById('data-fields', id),
    create: (data) => create('data-fields', data),
    update: (id, data) => update('data-fields', id, data),
    delete: (id) => deleteItem('data-fields', id),
};

export const organizationsApi = {
    getAll: () => getAll('organizations'),
    getById: (id) => getById('organizations', id),
    create: (data) => api.post('/organizations/', data).then((response) => parseResponseFields(response.data)),
    createManaged: (parentId, data) => api.post(`/organizations/${parentId}/managed/`, data).then((response) => parseResponseFields(response.data)),
    select: (organizationId) => api.post('/organizations/select/', { organization_id: organizationId }).then((response) => parseResponseFields(response.data)),
    getMyPermissions: () => api.get('/organizations/me/permissions/').then((response) => parseResponseFields(response.data)),
    getNotificationSettings: () => api.get('/organizations/me/settings/notifications/').then((response) => response.data),
    updateNotificationSettings: ({ setting_name, setting_key, enabled }) => api.put(
        '/organizations/me/settings/notifications/',
        { setting_name, setting_key, enabled },
    ).then((response) => response.data),
};

/** Current user's profile (display name, locale, phone). Email is read-only. */
export const userProfileApi = {
    get: async () => {
        const response = await api.get('/me/profile/');
        return response.data;
    },
    update: async (data) => {
        const response = await api.patch('/me/profile/', data);
        return response.data;
    },
};

/** WhatsApp app-level info (shared business number for adding as a contact). */
export const whatsappApi = {
    getBusinessInfo: async () => {
        const response = await api.get('/whatsapp/business-info/');
        return response.data;
    },
};

/** Analytics query API (docs/query.proto) — POST body { request_id?, spec } */
export const analyticsApi = {
    query: async (body) => {
        const response = await api.post('/analytics/query/', body);
        return parseResponseFields(response.data);
    },
};

/** Chat assistant API (Phase 1: read-only) */
export const chatApi = {
    createSession: async ({ locale = 'en' } = {}) => {
        const response = await api.post('/chat/sessions/', { locale });
        return response.data;
    },
    listSessions: async () => {
        const response = await api.get('/chat/sessions/');
        return response.data;
    },
    getActiveSession: async ({ locale = 'en' } = {}) => {
        const response = await api.post('/chat/sessions/active/', { locale });
        return response.data;
    },
    getSession: async (sessionId) => {
        const response = await api.get(`/chat/sessions/${sessionId}/`);
        return response.data;
    },
    updateSessionLocale: async (sessionId, locale) => {
        const response = await api.patch(`/chat/sessions/${sessionId}/`, { locale });
        return response.data;
    },
    deleteSession: async (sessionId) => {
        await api.delete(`/chat/sessions/${sessionId}/`);
    },
    listMessages: async (sessionId) => {
        const response = await api.get(`/chat/sessions/${sessionId}/messages/`);
        return response.data;
    },
    sendMessage: async (sessionId, content, context = null) => {
        const body = { content };
        if (context) {
            body.context = context;
        }
        const response = await api.post(`/chat/sessions/${sessionId}/messages/`, body, {
            validateStatus: (status) => status === 200 || status === 202,
        });
        if (response.status === 202) {
            return { async: true, ...response.data };
        }
        return { async: false, ...response.data };
    },
};